import { useMutation, useQuery } from "@apollo/client";
import Card from "components/Card/Card";
import Input from "components/Form/El/Input";
import { setNotification } from "helpers/cache";
import { convertDateToUSFormat } from "helpers/utils";
import { FC, useEffect, useMemo, useState } from "react";
import { CREATE_CLIENT_COMMENT } from "../../Services/Mutations/ClientCommentsMutation";
import { CLIENT_COMMENTS_BY_CLIENT_QUERY } from "../../Services/Queries/ClientCommentsQuery";

const COMMENTS_PER_PAGE = 25;
const COMMENT_MAX_LENGTH = 10000;

interface ClientComment {
  id: number;
  text: string;
  user: {
    id: number;
    userName: string;
  } | null;
  createdAt: string;
  updatedAt?: string;
}

interface ClientCommentsData {
  clientCommentsByClient: ClientComment[];
}

interface ClientCommentsVariables {
  clientId: number;
  limit: number;
  page: number;
}

const EditClientComments: FC<{
  clientId: number;
  access: any;
}> = ({ clientId, access }) => {
  const [commentText, setCommentText] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const canAddComment = useMemo(
    () =>
      !!access?.fields?.text?.editable &&
      access?.buttons?.includes("addComment"),
    [access],
  );

  const { data, loading, error, fetchMore, refetch } = useQuery<
    ClientCommentsData,
    ClientCommentsVariables
  >(CLIENT_COMMENTS_BY_CLIENT_QUERY, {
    variables: {
      clientId,
      limit: COMMENTS_PER_PAGE,
      page: 1,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [createClientComment, { loading: isSaving }] = useMutation(
    CREATE_CLIENT_COMMENT,
  );

  const comments = data?.clientCommentsByClient || [];

  useEffect(() => {
    setPage(1);
    setHasMore(false);
    setCommentText("");
  }, [clientId]);

  useEffect(() => {
    if (!loading && page === 1) {
      setHasMore(comments.length === COMMENTS_PER_PAGE);
    }
  }, [comments.length, loading, page]);

  const onCreateComment = async () => {
    const text = commentText.trim();
    if (!text || isSaving) return;

    try {
      await createClientComment({ variables: { clientId, text } });
      setCommentText("");
      setPage(1);

      const refreshed = await refetch({
        clientId,
        limit: COMMENTS_PER_PAGE,
        page: 1,
      });
      setHasMore(
        refreshed.data.clientCommentsByClient.length === COMMENTS_PER_PAGE,
      );
      setNotification([
        { type: "SUCCESS", message: "Client activity successfully added" },
      ]);
    } catch (mutationError) {
      setNotification([
        {
          type: "ERROR",
          message:
            mutationError instanceof Error
              ? mutationError.message
              : "Unable to add client activity",
        },
      ]);
    }
  };

  const onLoadOlder = async () => {
    if (isLoadingOlder) return;

    const nextPage = page + 1;
    setIsLoadingOlder(true);

    try {
      const result = await fetchMore({
        variables: {
          clientId,
          limit: COMMENTS_PER_PAGE,
          page: nextPage,
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          const olderComments = fetchMoreResult?.clientCommentsByClient || [];
          const knownIds = new Set(
            previous.clientCommentsByClient.map((comment) => comment.id),
          );

          return {
            clientCommentsByClient: [
              ...previous.clientCommentsByClient,
              ...olderComments.filter((comment) => !knownIds.has(comment.id)),
            ],
          };
        },
      });

      const olderComments = result.data.clientCommentsByClient || [];
      setPage(nextPage);
      setHasMore(olderComments.length === COMMENTS_PER_PAGE);
    } catch (queryError) {
      setNotification([
        {
          type: "ERROR",
          message:
            queryError instanceof Error
              ? queryError.message
              : "Unable to load older client activity",
        },
      ]);
    } finally {
      setIsLoadingOlder(false);
    }
  };

  return (
    <Card
      title={
        <span>
          <i className="icon-comment pr-1" />
          Client Activity
        </span>
      }
      className="box-4 client-activity-card"
      contentClassName="client-activity-card__content"
    >
      <div className="client-activity-list" aria-live="polite">
        {loading && page === 1 && (
          <div className="client-activity-state">
            <i className="inline-block animate-spin icon-spin4 pr-1" />
            Loading activity...
          </div>
        )}

        {!loading && error && (
          <div className="client-activity-state red">
            Unable to load client activity.
          </div>
        )}

        {!loading && !error && !comments.length && (
          <div className="client-activity-state">No client activity yet.</div>
        )}

        {comments.map((comment) => (
          <article className="client-activity-item" key={comment.id}>
            <div className="client-activity-item__meta">
              <strong>{comment.user?.userName || "Former User"}</strong>
              <time dateTime={comment.createdAt}>
                {convertDateToUSFormat(comment.createdAt)}
              </time>
            </div>
            <div className="client-activity-item__text">{comment.text}</div>
          </article>
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          className="btn btn-small btn-blue client-activity-load"
          disabled={isLoadingOlder}
          onClick={onLoadOlder}
        >
          <i
            className={`${
              isLoadingOlder
                ? "inline-block animate-spin icon-spin4"
                : "icon-down-open"
            } pr-1`}
          />
          Load Older
        </button>
      )}

      {canAddComment && (
        <div className="client-activity-composer">
          <Input
            value={commentText}
            onChange={(value) =>
              setCommentText(
                value.toString().slice(0, COMMENT_MAX_LENGTH),
              )
            }
            name="client-activity-comment"
            placeholder="Add client activity..."
            isTextarea
            rowCount={3}
            disabled={isSaving}
          />
          <div className="client-activity-composer__footer">
            <span>{commentText.length.toLocaleString()}/10,000</span>
            <button
              type="button"
              className="btn btn-small btn-success"
              disabled={isSaving || !commentText.trim()}
              onClick={onCreateComment}
            >
              <i
                className={`${
                  isSaving
                    ? "inline-block animate-spin icon-spin4"
                    : "icon-plus"
                } pr-1`}
              />
              Add Comment
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EditClientComments;
